import React, { useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { ThemeContext } from '../../contexts/ThemeContext';
import { Helmet } from 'react-helmet';

const MultiTenantOperator = () => {
  const { theme } = useContext(ThemeContext);
  
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      mirror: false,
      easing: 'ease-out-cubic'
    });
    
    return () => {
      AOS.refresh();
    };
  }, []);
  
  return (
    <div className="py-16 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950 min-h-screen">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-extrabold text-gray-800 dark:text-blue-50 mb-8" data-aos="fade-up">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-teal-500">Multi Tenant</span> Operator
          </h1>
          
          <div className="bg-gradient-to-r from-indigo-600 via-blue-700 to-teal-600 text-white p-8 md:p-10 rounded-xl shadow-xl mb-12 transform transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl" data-aos="zoom-in">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Advanced Multi-Tenancy for Kubernetes Environments</h2>
            <p className="text-lg md:text-xl">
              Fepsec's Multi Tenant Operator automates the management of shared Kubernetes environments with enterprise-grade isolation, security, and resource optimization.
            </p>
          </div>
          
          <div className="mb-12" data-aos="fade-up">
            <h3 className="text-3xl font-bold text-gray-800 dark:text-white mb-6 flex items-center">
              <span className="inline-block mr-3 bg-blue-600 rounded-full p-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </span>
              Multi-Tenancy Excellence
            </h3>
            <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed mb-6">
              Managing multiple teams, applications, or customers on shared Kubernetes infrastructure presents significant challenges. 
              Fepsec's Multi-Tenant Operator transforms these challenges into opportunities for cost savings, operational efficiency, 
              and enhanced security, all while avoiding common pitfalls like cluster sprawl and insufficient isolation.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-blue-600" data-aos="fade-right" data-aos-delay="100">
                <h4 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                  <span className="text-blue-600 mr-2">⟡</span> Cost Optimization
                </h4>
                <p className="text-gray-600">
                  Dramatically reduce infrastructure costs through intelligent resource sharing that maximizes utilization while maintaining performance guarantees for each tenant.
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-teal-600" data-aos="fade-left" data-aos-delay="200">
                <h4 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                  <span className="text-teal-600 mr-2">⟡</span> Operational Simplicity
                </h4>
                <p className="text-gray-600">
                  Replace the complexity of managing multiple clusters with a unified control plane that provides standardized operations while preserving tenant autonomy.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-indigo-600" data-aos="fade-right" data-aos-delay="300">
                <h4 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                  <span className="text-indigo-600 mr-2">⟡</span> Enhanced Security
                </h4>
                <p className="text-gray-600">
                  Implement defense-in-depth security with consistent policy enforcement across all tenants while maintaining strict isolation between sensitive workloads.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-purple-600" data-aos="fade-left" data-aos-delay="400">
                <h4 className="text-xl font-bold text-gray-800 mb-3 flex items-center">
                  <span className="text-purple-600 mr-2">⟡</span> Enterprise Governance
                </h4>
                <p className="text-gray-600">
                  Establish automated compliance checking and comprehensive audit trails across all tenants while providing fine-grained visibility for both platform administrators and tenant owners.
                </p>
              </div>
            </div>
          </div>
          
          <div className="mb-16" data-aos="fade-up">
            <h3 className="text-3xl font-bold text-gray-800 dark:text-white mb-6 flex items-center">
              <span className="inline-block mr-3 bg-teal-600 rounded-full p-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                </svg>
              </span>
              Fepsec Multi Tenant Operator
            </h3>
            
            <div className="bg-white rounded-xl overflow-hidden shadow-xl mb-8 transform transition-all duration-500 hover:shadow-2xl" data-aos="zoom-in-up">
              <div className="bg-gradient-to-r from-blue-600 to-teal-500 text-white p-6">
                <h4 className="text-2xl font-bold mb-2">Enterprise-Grade Multi-Tenancy</h4>
                <p className="text-lg opacity-90">
                  Our operator is a Kubernetes-native solution that automates and simplifies the management of multi-tenant environments with strong isolation,
                  intelligent resource allocation, and standardized operations across your infrastructure.
                </p>
              </div>
              <div className="p-6">
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Designed for organizations with complex infrastructure requirements, Fepsec's Multi Tenant Operator eliminates the operational overhead of 
                  managing multiple clusters while providing enterprise-grade security, governance, and optimized resource utilization.
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div className="bg-white p-6 rounded-xl shadow-lg transform transition-all duration-300 hover:-translate-y-1 hover:shadow-xl border-t-4 border-blue-600" data-aos="fade-up" data-aos-delay="100">
                <div className="flex items-center mb-4">
                  <div className="rounded-full bg-blue-100 p-3 mr-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-gray-800">Advanced Namespace Management</h4>
                </div>
                <p className="text-gray-600 pl-16">
                  Automated provisioning with intelligent namespace configuration that ensures proper isolation and security controls from creation to deletion.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-lg transform transition-all duration-300 hover:-translate-y-1 hover:shadow-xl border-t-4 border-teal-600" data-aos="fade-up" data-aos-delay="200">
                <div className="flex items-center mb-4">
                  <div className="rounded-full bg-teal-100 p-3 mr-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-gray-800">Dynamic Resource Optimization</h4>
                </div>
                <p className="text-gray-600 pl-16">
                  Intelligent resource allocation and real-time monitoring with adaptive quotas that automatically adjust based on actual usage patterns and business priorities.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-lg transform transition-all duration-300 hover:-translate-y-1 hover:shadow-xl border-t-4 border-indigo-600" data-aos="fade-up" data-aos-delay="300">
                <div className="flex items-center mb-4">
                  <div className="rounded-full bg-indigo-100 p-3 mr-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-gray-800">Advanced Network Security</h4>
                </div>
                <p className="text-gray-600 pl-16">
                  Zero-trust network policies with automatic generation and enforcement of rules that control traffic between tenants while facilitating secure communication patterns.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-lg transform transition-all duration-300 hover:-translate-y-1 hover:shadow-xl border-t-4 border-purple-600" data-aos="fade-up" data-aos-delay="400">
                <div className="flex items-center mb-4">
                  <div className="rounded-full bg-purple-100 p-3 mr-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-gray-800">Fine-Grained Access Control</h4>
                </div>
                <p className="text-gray-600 pl-16">
                  Enterprise-grade RBAC implementation with automated role creation and assignment based on tenant profiles and customizable security policies.
                </p>
              </div>
            </div>
          </div>
          
          <div className="mb-16" data-aos="fade-up">
            <h3 className="text-3xl font-bold text-gray-800 dark:text-white mb-6 flex items-center">
              <span className="inline-block mr-3 bg-indigo-600 rounded-full p-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </span>
              Advanced Capabilities
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:shadow-xl hover:-translate-y-1" data-aos="fade-up" data-aos-delay="100">
                <div className="h-2 bg-gradient-to-r from-blue-500 to-teal-500"></div>
                <div className="p-6">
                  <div className="bg-blue-100 text-blue-600 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-gray-800 dark:text-blue-100 mb-3">Self-Service Portal</h4>
                  <p className="text-gray-600">
                    Intuitive tenant dashboard that enables teams to manage their resources within predefined limits, request additional capacity, and view their usage metrics.
                  </p>
                </div>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:shadow-xl hover:-translate-y-1" data-aos="fade-up" data-aos-delay="200">
                <div className="h-2 bg-gradient-to-r from-indigo-500 to-blue-500"></div>
                <div className="p-6">
                  <div className="bg-indigo-100 text-indigo-600 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-gray-800 dark:text-blue-100 mb-3">GitOps Integration</h4>
                  <p className="text-gray-600">
                    Seamless integration with GitOps workflows that enables infrastructure-as-code management of tenant configurations with automated reconciliation and version control.
                  </p>
                </div>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:shadow-xl hover:-translate-y-1" data-aos="fade-up" data-aos-delay="300">
                <div className="h-2 bg-gradient-to-r from-teal-500 to-green-500"></div>
                <div className="p-6">
                  <div className="bg-teal-100 text-teal-600 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-gray-800 dark:text-blue-100 mb-3">Advanced Observability</h4>
                  <p className="text-gray-600">
                    Comprehensive tenant-specific monitoring with segregated metrics, logs, and alerts that provide both isolation and visibility across your entire infrastructure.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:shadow-xl hover:-translate-y-1" data-aos="fade-up" data-aos-delay="400">
                <div className="h-2 bg-gradient-to-r from-purple-500 to-indigo-500"></div>
                <div className="p-6">
                  <div className="bg-purple-100 text-purple-600 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-gray-800 dark:text-blue-100 mb-3">Policy Enforcement</h4>
                  <p className="text-gray-600">
                    Custom policy framework that enables consistent enforcement of security, compliance, and governance controls across all tenants without manual intervention.
                  </p>
                </div>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:shadow-xl hover:-translate-y-1" data-aos="fade-up" data-aos-delay="500">
                <div className="h-2 bg-gradient-to-r from-blue-500 to-purple-500"></div>
                <div className="p-6">
                  <div className="bg-blue-100 text-blue-600 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-gray-800 dark:text-blue-100 mb-3">Upgrade Management</h4>
                  <p className="text-gray-600">
                    Sophisticated upgrade orchestration that controls how and when tenant workloads receive updates, ensuring minimal disruption and maximum availability.
                  </p>
                </div>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:shadow-xl hover:-translate-y-1" data-aos="fade-up" data-aos-delay="600">
                <div className="h-2 bg-gradient-to-r from-green-500 to-emerald-500"></div>
                <div className="p-6">
                  <div className="bg-green-100 text-green-600 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-gray-800 dark:text-blue-100 mb-3">Cost Management</h4>
                  <p className="text-gray-600">
                    Advanced resource tracking with detailed cost attribution that enables accurate billing and chargeback for organizations with complex internal accounting requirements.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center" data-aos="fade-up">
            <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-teal-500 p-8 md:p-12 rounded-2xl shadow-xl mb-8 transform transition-all">
              <h3 className="text-3xl font-bold text-white mb-6">Ready to Transform Your Multi-Tenant Infrastructure?</h3>
              <p className="text-white text-lg md:text-xl mb-8 max-w-3xl mx-auto opacity-90">
                Contact our team to learn how Fepsec's Multi Tenant Operator can revolutionize your Kubernetes environment with advanced isolation, optimized resource utilization, and enterprise-grade security.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link 
                  to="/contact-us" 
                  className="inline-block bg-white text-blue-600 hover:bg-gray-100 font-bold px-8 py-4 rounded-lg transition transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  Schedule a Consultation
                </Link>
                <Link 
                  to="/services" 
                  className="inline-block bg-transparent text-white border-2 border-white hover:bg-white/10 font-bold px-8 py-4 rounded-lg transition transform hover:scale-105"
                >
                  View All Services
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiTenantOperator;
